import { OwcGrid, OwcTypography } from "@one/react"
import CustomChip from "../CustomChip"
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import ActionType from "./BookingEventDetails/ActionType";
import ApprovalStatus from "./BookingEventDetails/ApprovalStatus";
import AssistanceDetails from "./BookingEventDetails/AssistanceDetails";
import BookedForUsers from "./BookingEventDetails/BookedForUsers";
import DateTimeRange from "./BookingEventDetails/DateTimeRange";
import ElnDetails from "./BookingEventDetails/ElnDetails";
import InlineEditableField from "../InlineEditableField/InlineEditableField";
import { QUICK_EDIT_BOOKING } from "../../../gql/bookingapi/mutations";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { quickEditTracker } from "../../../features/booking-operations/quickEditTracker";
import { formatUserDisplay, USER_FORMAT_PRESETS } from "../../../utils/helpers/text";
import ProjectInfo from "./BookingEventDetails/ProjectInfo";

const BookingEventDetails = ({
  eventData,
  title = 'Event Details',
  client,
  onUpdateComplete,
  onAssistanceUpdateStarted,
  onAssistanceUpdateComplete,
  assistanceUpdateCallbackRef,
  allowAssistanceEdit = true,
  startLoading,
  stopLoading
}) => {
  const user = useSelector((store) => store.user);
  const usersList = useSelector((state) => state.users.users);
  const assistantName = (() => {
    if (!eventData?.assistent || !eventData?.assistent[0]) return "Not yet assigned";

    const assistantId = eventData.assistent[0];
    const assistantUser = usersList?.find(user => user.userId === assistantId);
    if (assistantUser) {
      return formatUserDisplay(assistantUser, USER_FORMAT_PRESETS.nameOnly);
    }

    return assistantId;
  })();

  const [descriptionValue, setDescriptionValue] = useState(eventData?.description ?? "");
  const [hazardousValue, setHazardousValue] = useState(eventData?.hazardousDeclaration?.hazardousMaterialsList ?? "");

  useEffect(() => {
    setDescriptionValue(eventData?.description ?? "");
  }, [eventData?.description]);

  useEffect(() => {
    setHazardousValue(eventData?.hazardousDeclaration?.hazardousMaterialsList ?? "");
  }, [eventData?.hazardousDeclaration?.hazardousMaterialsList]);

  const isBookingAdmin = Array.isArray(user?.roles) && user.roles.some(r => r === "BookingAdmin");
  const canEditInline = !!eventData?.id && (isBookingAdmin);

  const [hasShownMultiSlotWarning, setHasShownMultiSlotWarning] = useState(false);
  const handleInlineEditStart = () => {
    const slotCount = eventData?.bookingEntries?.length || 0;
    if (slotCount > 1 && !hasShownMultiSlotWarning) {
      try {
        snackbarService.show({
          type: "warning",
          message: `This booking has ${slotCount} slots. Editing will affect all slots in the booking.`,
          duration: 5000
        });
      } catch (_) { }
      setHasShownMultiSlotWarning(true);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '1.125rem 0' }}>
        <OwcTypography variant="button">
          {title}
        </OwcTypography>
        {(() => {
          const entries = Array.isArray(eventData?.bookingEntries) ? [...eventData.bookingEntries] : [];
          entries.sort((a, b) => new Date(a.start) - new Date(b.start));
          return (
            <CustomChip
              text={`${entries.length}`}
              color="#fff"
              bgColor="rgba(11, 65, 205, 1)"
              compact
              info="Timeslot position within booking"
            />
          );
        })()}
      </div>

      <OwcGrid
        container
        columns={{ xs: 1, s: 2, m: 2, l: 2, xl: 2 }}
        vGutter={{ m: 8, l: 8, xl: 8, xs: 8, s: 8 }}
        hGutter={0}
      >
        <OwcGrid item l={1} m={1} xs={2} s={2} style={{
          alignSelf: 'start',
          paddingBottom: '0.25rem'
        }}>
          <DateTimeRange
            start={eventData?.start}
            end={eventData?.end}
            timezone={user?.timezone}
            repetitionPattern={eventData?.repetitionPattern}
          />
        </OwcGrid>


        {eventData.type === 'Run' && (
          <OwcGrid item xl={1} l={1} m={1} xs={1} s={1} style={{
            alignSelf: 'start',
            paddingBottom: '0.25rem'
          }}>
            <ApprovalStatus approvalStatus={eventData?.approval?.status} />
          </OwcGrid>
        )}

        <OwcGrid item xl={1} l={1} m={1} xs={1} s={2} style={{
          alignSelf: 'start',
          paddingBottom: '0.25rem'
        }}>
          <InlineEditableField
            label="Description"
            value={descriptionValue}
            allowEdit={canEditInline}
            placeholder="Enter description"
            onEditStart={handleInlineEditStart}
            onSave={async (nextVal) => {
              const bookingId = eventData?.bookingId || eventData?.id || eventData?.entryId;
              if (!bookingId) return;
              const trimmed = nextVal.trim();
              if (trimmed === (descriptionValue || "").trim()) {
                return;
              }

              const previousValue = descriptionValue;
              if (startLoading) startLoading();

              try {
                const result = await client.mutate({
                  mutation: QUICK_EDIT_BOOKING,
                  variables: { input: { bookingId, description: trimmed, createdByEmail: user?.email } },
                });
                const payload = result?.data?.quickEditBooking;
                if (!payload?.success) {
                  if (stopLoading) stopLoading();
                  setDescriptionValue(previousValue);
                  try {
                    snackbarService.show({
                      type: "error",
                      message: payload?.message || 'Description update failed',
                      duration: 5000
                    });
                  } catch (_) { }
                  return;
                }

                setDescriptionValue(trimmed);

                if (payload?.commandId) {
                  quickEditTracker.register(payload.commandId, 'UPDATE', {
                    onSuccess: () => {
                      if (stopLoading) stopLoading();
                      if (onUpdateComplete) onUpdateComplete();
                    },
                    onError: () => {
                      if (stopLoading) stopLoading();
                      setDescriptionValue(previousValue);
                    }
                  }).catch(() => { });
                } else {
                  if (stopLoading) stopLoading();
                  if (onUpdateComplete) onUpdateComplete();
                }
              } catch (error) {
                if (stopLoading) stopLoading();
                setDescriptionValue(previousValue);
                try {
                  snackbarService.show({
                    type: "error",
                    message: error?.message || 'Description update failed',
                    duration: 5000
                  });
                } catch (_) { }
              }
            }}
            displayFormatter={(val) => (val && String(val).trim() ? String(val) : "-")}
          />
        </OwcGrid>

        {eventData.type === 'Run' && (
          <OwcGrid item xl={1} l={1} m={1} xs={1} s={2} style={{
            alignSelf: 'start',
            paddingBottom: '0.25rem'
          }}>
            <InlineEditableField
              label="Hazardous materials details"
              value={hazardousValue}
              allowEdit={canEditInline && !!eventData?.hazardousDeclaration?.hasHazardousMaterials}
              placeholder="Enter hazardous materials list"
              onEditStart={handleInlineEditStart}
              onSave={async (nextVal) => {
                const bookingId = eventData?.bookingId || eventData?.id || eventData?.entryId;
                if (!bookingId) return;
                const trimmed = nextVal.trim();
                if (trimmed === (hazardousValue || "").trim()) return;

                const previousValue = hazardousValue;
                if (startLoading) startLoading();

                try {
                  const result = await client.mutate({
                    mutation: QUICK_EDIT_BOOKING,
                    variables: { input: { bookingId, hazardousDeclaration: { hasHazardousMaterials: !!eventData?.hazardousDeclaration?.hasHazardousMaterials, hazardousMaterialsList: trimmed }, createdByEmail: user?.email } },
                  });
                  const payload = result?.data?.quickEditBooking;
                  if (!payload?.success) {
                    if (stopLoading) stopLoading();
                    setHazardousValue(previousValue);
                    try {
                      snackbarService.show({
                        type: "error",
                        message: payload?.message || 'Hazardous materials update failed',
                        duration: 5000
                      });
                    } catch (_) { }
                    return;
                  }

                  setHazardousValue(trimmed);

                  if (payload?.commandId) {
                    quickEditTracker.register(payload.commandId, 'UPDATE', {
                      onSuccess: () => {
                        if (stopLoading) stopLoading();
                        if (onUpdateComplete) onUpdateComplete();
                      },
                      onError: () => {
                        if (stopLoading) stopLoading();
                        setHazardousValue(previousValue);
                      }
                    }).catch(() => { });
                  } else {
                    if (stopLoading) stopLoading();
                    if (onUpdateComplete) onUpdateComplete();
                  }
                } catch (error) {
                  if (stopLoading) stopLoading();
                  setHazardousValue(previousValue);
                  try {
                    snackbarService.show({
                      type: "error",
                      message: error?.message || 'Hazardous materials update failed',
                      duration: 5000
                    });
                  } catch (_) { }
                }
              }}
              displayFormatter={(val) => (val && String(val).trim() ? String(val) : "-")}
            />
          </OwcGrid>
        )}

        <OwcGrid xl={1} item l={1} m={1} xs={1} s={2} style={{
          alignSelf: 'start',
          paddingBottom: '0.25rem'
        }}>
          <ProjectInfo
            projectId={eventData?.project}
            redPandaProjects={eventData?.redPandaProjects}
            client={client}
            user={user}
          />
        </OwcGrid>

        {eventData.type === 'Run' && (
          <OwcGrid item xl={1} l={1} m={1} xs={1} s={2} style={{
            alignSelf: 'start',
            paddingBottom: '0.25rem'
          }}>
            <AssistanceDetails
              assistanceDeclaration={eventData?.assistanceDeclaration}
              assistanceDuration={eventData?.assistentTime}
              assistantName={assistantName}
              approvalStatus={eventData?.approval?.status}
              client={client}
              booking={eventData}
              onUpdateComplete={onUpdateComplete}
              onAssistanceUpdateStarted={onAssistanceUpdateStarted}
              onAssistanceUpdateComplete={onAssistanceUpdateComplete}
              assistanceUpdateCallbackRef={assistanceUpdateCallbackRef}
              allowEdit={allowAssistanceEdit}
              startLoading={startLoading}
              stopLoading={stopLoading}
            />
          </OwcGrid>
        )}


        {eventData.type === 'Run' && (
          <OwcGrid item xl={1} l={1} m={1} xs={1} s={2} style={{
            alignSelf: 'start',
            paddingBottom: '0.25rem'
          }}>
            <ElnDetails elnId={eventData?.elnId} />
          </OwcGrid>
        )}


        <OwcGrid item xl={1} l={1} m={1} xs={1} s={1} style={{
          alignSelf: 'start',
          paddingBottom: '0.25rem'
        }}>
          <ActionType type={eventData?.type} />
        </OwcGrid>

        <OwcGrid item xl={2} l={2} m={2} s={2} xs={2} style={{
          alignSelf: 'start',
          paddingBottom: '0.25rem'
        }}>
          <BookedForUsers
            bookedForEmails={eventData?.reservedForEmails}
            usersList={usersList}
          />
        </OwcGrid>

      </OwcGrid>
    </>
  )
}

export default BookingEventDetails
